import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    LayoutTemplate, Plus, Search, Trash2, ArrowUp, ArrowDown,
    Save, X, Archive, ArchiveRestore, Eye, Languages,
} from 'lucide-react';
import {
    apiService, TreatmentNoteTemplate, TemplateSection, TemplateCategory,
    SectionType, SupportedLocale, I18nString, SectionOption, Indication,
    CreateTemplatePayload, UpdateTemplatePayload,
} from '../../services/apiService';
import { pickLocale, SUPPORTED_LOCALES } from '../../utils/i18n';
import { useToast } from '../../contexts/ToastContext';
import { renderTemplate } from './notes/templateRenderer';

const CATEGORY_LABEL_KEY: Record<TemplateCategory, string> = {
    prophylaxis: 'templates.categoryProphylaxis',
    restorative: 'templates.categoryRestorative',
    endodontics: 'templates.categoryEndodontics',
    implant: 'templates.categoryImplant',
    aesthetics: 'templates.categoryAesthetics',
    consultation: 'templates.categoryConsultation',
    emergency: 'templates.categoryEmergency',
    diagnostic: 'templates.categoryDiagnostic',
    other: 'templates.categoryOther',
};

const SECTION_TYPES: { value: SectionType; labelKey: string; helperKey: string }[] = [
    { value: 'single_choice', labelKey: 'templates.sectionTypeSingleChoiceLabel', helperKey: 'templates.sectionTypeSingleChoiceHelper' },
    { value: 'multi_choice', labelKey: 'templates.sectionTypeMultiChoiceLabel', helperKey: 'templates.sectionTypeMultiChoiceHelper' },
    { value: 'tooth_chart', labelKey: 'templates.sectionTypeToothChartLabel', helperKey: 'templates.sectionTypeToothChartHelper' },
    { value: 'text_freeform', labelKey: 'templates.sectionTypeTextFreeformLabel', helperKey: 'templates.sectionTypeTextFreeformHelper' },
    { value: 'chips_indication', labelKey: 'templates.sectionTypeChipsIndicationLabel', helperKey: 'templates.sectionTypeChipsIndicationHelper' },
];

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';
const cardClass = 'rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5';

// -----------------------------------------------------------------------------
// LocalesContext — globaler Toggle pro Admin-Detail-Form, ob ES/EN-Felder
// sichtbar sind. Default nur DE (Praxis ist deutsch). User kann ES/EN per
// Klick hinzuschalten, falls Patient-Doku in Spanisch/Englisch nötig wird.
// -----------------------------------------------------------------------------

const LocalesContext = createContext<SupportedLocale[]>(['de']);

// -----------------------------------------------------------------------------
// i18n input row — Anzahl Spalten haengt vom LocalesContext ab
// -----------------------------------------------------------------------------

const I18nRow: React.FC<{
    value: I18nString;
    onChange: (next: I18nString) => void;
    placeholder?: string;
    type?: 'input' | 'textarea';
    rows?: number;
}> = ({ value, onChange, placeholder, type = 'input', rows = 2 }) => {
    const visibleLocales = useContext(LocalesContext);
    const set = (locale: SupportedLocale) => (v: string) => onChange({ ...value, [locale]: v });
    const gridCols = visibleLocales.length === 1 ? 'grid-cols-1' : visibleLocales.length === 2 ? 'grid-cols-2' : 'grid-cols-3';
    return (
        <div className={`grid ${gridCols} gap-2`}>
            {visibleLocales.map(l => (
                <div key={l}>
                    {visibleLocales.length > 1 && (
                        <div className="text-[10px] font-medium uppercase text-brand-text-muted mb-1">{l}</div>
                    )}
                    {type === 'textarea' ? (
                        <textarea
                            value={value[l] || ''}
                            onChange={e => set(l)(e.target.value)}
                            placeholder={placeholder}
                            rows={rows}
                            className={`${inputClass} resize-y`}
                        />
                    ) : (
                        <input
                            type="text"
                            value={value[l] || ''}
                            onChange={e => set(l)(e.target.value)}
                            placeholder={placeholder}
                            className={inputClass}
                        />
                    )}
                </div>
            ))}
        </div>
    );
};

// -----------------------------------------------------------------------------
// Section Option Editor (single_choice / multi_choice)
// -----------------------------------------------------------------------------

const OptionsEditor: React.FC<{
    options: SectionOption[];
    onChange: (next: SectionOption[]) => void;
}> = ({ options, onChange }) => {
    const { t } = useTranslation();
    const setAt = (i: number, next: SectionOption) => {
        const copy = options.slice();
        copy[i] = next;
        onChange(copy);
    };
    const remove = (i: number) => onChange(options.filter((_, j) => j !== i));
    const add = () => onChange([...options, { value: '', label: { de: '', es: '', en: '' } }]);
    return (
        <div className="space-y-2">
            {options.map((o, i) => (
                <div key={i} className="rounded-md border border-brand-border bg-brand-bg/40 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                        <div className="flex-1">
                            <input
                                type="text"
                                value={o.value}
                                onChange={e => setAt(i, { ...o, value: e.target.value })}
                                placeholder={t('templates.optionValuePlaceholder')}
                                className={inputClass + ' font-mono text-xs'}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            className="p-1.5 text-brand-text-muted hover:text-red-600 hover:bg-red-50 rounded cursor-pointer transition-colors"
                            aria-label={t('templates.removeOption')}
                            title={t('templates.removeOption')}
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    <I18nRow value={o.label} onChange={lbl => setAt(i, { ...o, label: lbl })} placeholder={t('templates.optionLabelPlaceholder')} />
                </div>
            ))}
            <button
                type="button"
                onClick={add}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-brand-text-muted hover:text-brand-text border border-dashed border-brand-border hover:border-brand-primary rounded cursor-pointer transition-colors"
            >
                <Plus className="w-3 h-3" /> {t('templates.addOption')}
            </button>
        </div>
    );
};

// -----------------------------------------------------------------------------
// Section Editor
// -----------------------------------------------------------------------------

const SectionEditor: React.FC<{
    section: TemplateSection;
    index: number;
    total: number;
    onChange: (next: TemplateSection) => void;
    onRemove: () => void;
    onMove: (delta: -1 | 1) => void;
}> = ({ section, index, total, onChange, onRemove, onMove }) => {
    const { t } = useTranslation();
    const updateConfig = (patch: Record<string, any>) => {
        onChange({ ...section, config: { ...(section.config || {}), ...patch } });
    };
    return (
        <div className="rounded-xl border border-brand-border bg-brand-surface-2/40 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-brand-text-muted">
                    <span className="px-2 py-0.5 rounded bg-brand-border/40 text-brand-text font-mono">#{index + 1}</span>
                    <span className="font-medium uppercase tracking-wider">{section.type}</span>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => onMove(-1)}
                        disabled={index === 0}
                        className="p-1 text-brand-text-muted hover:text-brand-text disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer rounded transition-colors"
                        title={t('templates.moveUp')}
                    >
                        <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => onMove(1)}
                        disabled={index === total - 1}
                        className="p-1 text-brand-text-muted hover:text-brand-text disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer rounded transition-colors"
                        title={t('templates.moveDown')}
                    >
                        <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={onRemove}
                        className="p-1 text-brand-text-muted hover:text-red-600 hover:bg-red-50 cursor-pointer rounded transition-colors"
                        title={t('templates.removeSection')}
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>{t('templates.sectionKey')}</label>
                    <input
                        type="text"
                        value={section.key}
                        onChange={e => onChange({ ...section, key: e.target.value })}
                        placeholder={t('templates.sectionKeyPlaceholder')}
                        className={inputClass + ' font-mono text-xs'}
                    />
                </div>
                <div>
                    <label className={labelClass}>{t('templates.sectionTypeField')}</label>
                    <input
                        type="text"
                        value={section.type}
                        readOnly
                        className={inputClass + ' opacity-60 cursor-not-allowed font-mono text-xs'}
                    />
                </div>
            </div>

            <div>
                <label className={labelClass}>{t('templates.sectionLabel')}</label>
                <I18nRow value={section.label} onChange={lbl => onChange({ ...section, label: lbl })} placeholder={t('templates.sectionLabelPlaceholder')} />
            </div>

            <div>
                <label className={labelClass}>{t('templates.docTemplateLabel')}</label>
                <p className="text-[10px] text-brand-text-muted mb-1.5">
                    {t('templates.docTemplateVars', { single: '{value_label}', multi: '{values_csv}', tooth: '{teeth_csv}', text: '{text}' })}
                </p>
                <I18nRow
                    value={section.doc_template}
                    onChange={tpl => onChange({ ...section, doc_template: tpl })}
                    type="textarea"
                    rows={2}
                    placeholder={t('templates.docTemplatePlaceholder')}
                />
            </div>

            {(section.type === 'single_choice' || section.type === 'multi_choice') && (
                <div>
                    <label className={labelClass}>{t('templates.options')}</label>
                    <OptionsEditor
                        options={section.options || []}
                        onChange={opts => onChange({ ...section, options: opts })}
                    />
                </div>
            )}

            {section.type === 'tooth_chart' && (
                <div>
                    <label className="inline-flex items-center gap-2 text-xs text-brand-text-muted cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={(section.config as any)?.allow_deciduous === true}
                            onChange={e => updateConfig({ allow_deciduous: e.target.checked })}
                            className="rounded border-brand-border cursor-pointer"
                        />
                        {t('templates.showDeciduousToggle')}
                    </label>
                </div>
            )}

            {section.type === 'text_freeform' && (
                <div>
                    <label className={labelClass}>{t('templates.placeholderI18n')}</label>
                    <I18nRow
                        value={((section.config as any)?.placeholder || {}) as I18nString}
                        onChange={ph => updateConfig({ placeholder: ph })}
                        placeholder={t('templates.placeholderHint')}
                    />
                </div>
            )}
        </div>
    );
};

// -----------------------------------------------------------------------------
// New Section Type Picker
// -----------------------------------------------------------------------------

const newSection = (type: SectionType): TemplateSection => {
    const base: TemplateSection = {
        key: `section_${Math.random().toString(36).slice(2, 7)}`,
        type,
        label: { de: '', es: '', en: '' },
        doc_template: { de: '', es: '', en: '' },
    };
    if (type === 'single_choice' || type === 'multi_choice') base.options = [];
    if (type === 'tooth_chart') base.config = { allow_deciduous: false };
    if (type === 'text_freeform') base.config = { placeholder: { de: '', es: '', en: '' } };
    return base;
};

// -----------------------------------------------------------------------------
// Template Detail Form
// -----------------------------------------------------------------------------

const emptyTemplate = (): Partial<TreatmentNoteTemplate> => ({
    slug: '',
    name: { de: '', es: '', en: '' },
    category: 'other',
    language_default: 'de',
    header_template: { de: '{title}\nBehandler: {practitioner_name}', es: '', en: '' },
    sections: [],
});

interface DetailProps {
    draft: Partial<TreatmentNoteTemplate>;
    isNew: boolean;
    indications: Indication[];
    submitting: boolean;
    onChange: (next: Partial<TreatmentNoteTemplate>) => void;
    onSave: () => void;
    onArchive: () => void;
    onUnarchive: () => void;
    onCancel: () => void;
}

const TemplateDetailForm: React.FC<DetailProps> = ({
    draft, isNew, indications, submitting, onChange, onSave, onArchive, onUnarchive, onCancel,
}) => {
    const { t } = useTranslation();
    const [showAddType, setShowAddType] = useState(false);
    const [previewLocale, setPreviewLocale] = useState<SupportedLocale>('de');
    const [visibleLocales, setVisibleLocales] = useState<SupportedLocale[]>(['de']);
    const allLocales = visibleLocales.length === SUPPORTED_LOCALES.length;

    const sections = draft.sections || [];
    const updateSection = (i: number, next: TemplateSection) => {
        const copy = sections.slice();
        copy[i] = next;
        onChange({ ...draft, sections: copy });
    };
    const removeSection = (i: number) => {
        onChange({ ...draft, sections: sections.filter((_, j) => j !== i) });
    };
    const moveSection = (i: number, delta: -1 | 1) => {
        const j = i + delta;
        if (j < 0 || j >= sections.length) return;
        const copy = sections.slice();
        [copy[i], copy[j]] = [copy[j], copy[i]];
        onChange({ ...draft, sections: copy });
    };
    const addSection = (type: SectionType) => {
        onChange({ ...draft, sections: [...sections, newSection(type)] });
        setShowAddType(false);
    };

    // Demo-Preview: fuer jede Section ein Demo-Wert befuellen
    const demoSectionValues = useMemo(() => {
        const out: Record<string, any> = {};
        for (const s of sections) {
            if (s.type === 'single_choice' && s.options?.[0]) out[s.key] = s.options[0].value;
            else if (s.type === 'multi_choice' && s.options?.length) out[s.key] = s.options.slice(0, 2).map(o => o.value);
            else if (s.type === 'tooth_chart') out[s.key] = ['16', '26', '36', '46'];
            else if (s.type === 'text_freeform') out[s.key] = t('templates.demoFreetext');
            else if (s.type === 'chips_indication') out[s.key] = indications.slice(0, 2).map(i => i.value);
        }
        return out;
    }, [sections, indications]);

    const previewBody = useMemo(() => {
        if (!draft.slug && !draft.name) return '';
        return renderTemplate(
            { ...(draft as any), id: 0, version: 1, archived: false } as TreatmentNoteTemplate,
            demoSectionValues,
            previewLocale,
            { title: pickLocale(draft.name as I18nString, previewLocale), practitioner_name: 'admin', patient_name: 'Demo Patient' },
            indications,
        );
    }, [draft, demoSectionValues, previewLocale, indications]);

    return (
        <LocalesContext.Provider value={visibleLocales}>
        <div className="space-y-4">
            <div className={cardClass}>
                <div className="flex items-center justify-between mb-4 gap-3">
                    <h2 className="text-base font-semibold text-brand-text">
                        {isNew ? t('templates.newTemplateHeading') : (pickLocale(draft.name as I18nString, 'de') || draft.slug)}
                    </h2>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setVisibleLocales(allLocales ? ['de'] : (SUPPORTED_LOCALES as SupportedLocale[]).slice())}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-brand-text-muted hover:text-brand-text border border-brand-border rounded cursor-pointer transition-colors"
                            title={allLocales ? t('templates.langOnlyDeTitle') : t('templates.langAddEsEnTitle')}
                        >
                            <Languages className="w-3.5 h-3.5" />
                            {allLocales ? t('templates.langOnlyDe') : t('templates.langAll')}
                        </button>
                        {!isNew && (
                            draft.archived ? (
                                <button
                                    type="button"
                                    onClick={onUnarchive}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-brand-text-muted hover:text-brand-text border border-brand-border rounded cursor-pointer transition-colors"
                                >
                                    <ArchiveRestore className="w-3.5 h-3.5" /> {t('templates.restore')}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onArchive}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-brand-text-muted hover:text-red-600 border border-brand-border hover:border-red-200 rounded cursor-pointer transition-colors"
                                >
                                    <Archive className="w-3.5 h-3.5" /> {t('templates.archive')}
                                </button>
                            )
                        )}
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:text-brand-text cursor-pointer transition-colors"
                        >
                            {t('templates.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={submitting}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-primary text-white text-xs font-semibold rounded-md hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                        >
                            <Save className="w-3.5 h-3.5" />
                            {submitting ? t('templates.saving') : t('templates.save')}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <label className={labelClass}>{t('templates.slugLabel')}</label>
                        <input
                            type="text"
                            value={draft.slug || ''}
                            onChange={e => onChange({ ...draft, slug: e.target.value.toLowerCase() })}
                            placeholder={t('templates.slugPlaceholder')}
                            className={inputClass + ' font-mono text-xs'}
                            readOnly={!isNew}
                            title={!isNew ? t('templates.slugImmutableTitle') : undefined}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>{t('templates.categoryLabel')}</label>
                        <select
                            value={draft.category || 'other'}
                            onChange={e => onChange({ ...draft, category: e.target.value as TemplateCategory })}
                            className={inputClass + ' cursor-pointer'}
                        >
                            {(Object.keys(CATEGORY_LABEL_KEY) as TemplateCategory[]).map(c => (
                                <option key={c} value={c}>{t(CATEGORY_LABEL_KEY[c])}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="mb-3">
                    <label className={labelClass}>{t('templates.nameLabel')}</label>
                    <I18nRow
                        value={(draft.name || {}) as I18nString}
                        onChange={n => onChange({ ...draft, name: n })}
                        placeholder={t('templates.namePlaceholder')}
                    />
                </div>

                <div className="mb-3">
                    <label className={labelClass}>{t('templates.headerTemplateLabel')}</label>
                    <p className="text-[10px] text-brand-text-muted mb-1.5">
                        {t('templates.headerTemplateVars', { title: '{title}', practitioner: '{practitioner_name}', patient: '{patient_name}' })}
                    </p>
                    <I18nRow
                        value={(draft.header_template || {}) as I18nString}
                        onChange={h => onChange({ ...draft, header_template: h })}
                        type="textarea"
                        rows={2}
                    />
                </div>

                <div>
                    <label className={labelClass}>{t('templates.languageDefaultLabel')}</label>
                    <select
                        value={draft.language_default || 'de'}
                        onChange={e => onChange({ ...draft, language_default: e.target.value as SupportedLocale })}
                        className={inputClass + ' cursor-pointer max-w-xs'}
                    >
                        {SUPPORTED_LOCALES.map(l => (
                            <option key={l} value={l}>{l.toUpperCase()}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Sections */}
            <div className={cardClass}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-brand-text">
                        {t('templates.sectionsHeading', { count: sections.length })}
                    </h3>
                </div>
                <div className="space-y-3">
                    {sections.map((s, i) => (
                        <SectionEditor
                            key={i}
                            section={s}
                            index={i}
                            total={sections.length}
                            onChange={next => updateSection(i, next)}
                            onRemove={() => removeSection(i)}
                            onMove={delta => moveSection(i, delta)}
                        />
                    ))}
                    {sections.length === 0 && (
                        <p className="text-sm text-brand-text-muted text-center py-6">
                            {t('templates.noSections')}
                        </p>
                    )}
                </div>

                {showAddType ? (
                    <div className="mt-4 rounded-md border border-brand-primary/30 bg-brand-primary/5 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-brand-text">{t('templates.pickSectionType')}</span>
                            <button
                                type="button"
                                onClick={() => setShowAddType(false)}
                                className="text-brand-text-muted hover:text-brand-text cursor-pointer"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {SECTION_TYPES.map(st => (
                                <button
                                    key={st.value}
                                    type="button"
                                    onClick={() => addSection(st.value)}
                                    className="text-left px-3 py-2 rounded-md border border-brand-border bg-brand-surface hover:border-brand-primary hover:bg-brand-surface-2 cursor-pointer transition-colors"
                                >
                                    <div className="text-sm font-medium text-brand-text">{t(st.labelKey)}</div>
                                    <div className="text-[11px] text-brand-text-muted mt-0.5">{t(st.helperKey)}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setShowAddType(true)}
                        className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-primary border border-dashed border-brand-primary/40 hover:bg-brand-primary/5 rounded cursor-pointer transition-colors"
                    >
                        <Plus className="w-3.5 h-3.5" /> {t('templates.addSection')}
                    </button>
                )}
            </div>

            {/* Live-Preview */}
            <div className={cardClass}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-brand-text flex items-center gap-2">
                        <Eye className="w-4 h-4 text-brand-primary" />
                        {t('templates.previewHeading')}
                    </h3>
                    <select
                        value={previewLocale}
                        onChange={e => setPreviewLocale(e.target.value as SupportedLocale)}
                        className="text-xs px-2 py-1 border border-brand-border bg-brand-surface text-brand-text rounded-md cursor-pointer"
                    >
                        {SUPPORTED_LOCALES.map(l => (
                            <option key={l} value={l}>{l.toUpperCase()}</option>
                        ))}
                    </select>
                </div>
                <pre className="text-sm leading-relaxed text-brand-text whitespace-pre-wrap font-sans bg-brand-bg/40 rounded-md border border-brand-border p-4 min-h-[120px]">
                    {previewBody || (
                        <span className="text-brand-text-muted italic">
                            {t('templates.previewEmpty')}
                        </span>
                    )}
                </pre>
            </div>
        </div>
        </LocalesContext.Provider>
    );
};

// -----------------------------------------------------------------------------
// Hauptpage: Sidebar + Detail
// -----------------------------------------------------------------------------

const TemplatesAdminPage: React.FC = () => {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const [templates, setTemplates] = useState<TreatmentNoteTemplate[]>([]);
    const [indications, setIndications] = useState<Indication[]>([]);
    const [loading, setLoading] = useState(true);
    const [showArchived, setShowArchived] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);
    const [draft, setDraft] = useState<Partial<TreatmentNoteTemplate>>(emptyTemplate());
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const [tplRes, indRes] = await Promise.all([
                apiService.listTreatmentNoteTemplates({ includeArchived: true }),
                apiService.listIndications(),
            ]);
            setTemplates(tplRes.data.data || []);
            setIndications(indRes.data.data || []);
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('templates.errorLoad'), 'error');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return templates.filter(tpl => {
            if (!showArchived && tpl.archived) return false;
            if (!q) return true;
            const name = pickLocale(tpl.name, 'de').toLowerCase();
            return name.includes(q) || tpl.slug.toLowerCase().includes(q);
        });
    }, [templates, query, showArchived]);

    const selectTemplate = (t: TreatmentNoteTemplate) => {
        setSelectedId(t.id);
        setDraft({ ...t });
    };

    const startNew = () => {
        setSelectedId('new');
        setDraft(emptyTemplate());
    };

    const cancel = () => {
        setSelectedId(null);
        setDraft(emptyTemplate());
    };

    const save = async () => {
        try {
            setSubmitting(true);
            if (selectedId === 'new') {
                const payload: CreateTemplatePayload = {
                    slug: draft.slug || '',
                    name: (draft.name || {}) as I18nString,
                    category: draft.category,
                    language_default: draft.language_default,
                    header_template: draft.header_template as I18nString,
                    sections: draft.sections || [],
                };
                const res = await apiService.createTreatmentNoteTemplate(payload);
                addToast(t('templates.created'), 'success');
                setSelectedId(res.data.data.id);
                setDraft({ ...res.data.data });
            } else if (typeof selectedId === 'number') {
                const payload: UpdateTemplatePayload = {
                    slug: draft.slug,
                    name: draft.name as I18nString,
                    category: draft.category,
                    language_default: draft.language_default,
                    header_template: draft.header_template as I18nString,
                    sections: draft.sections,
                };
                const res = await apiService.updateTreatmentNoteTemplate(selectedId, payload);
                addToast(t('templates.updated'), 'success');
                setDraft({ ...res.data.data });
            }
            await load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('templates.errorSave'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const archive = async () => {
        if (typeof selectedId !== 'number') return;
        if (!window.confirm(t('templates.confirmArchive'))) return;
        try {
            await apiService.archiveTreatmentNoteTemplate(selectedId);
            addToast(t('templates.archived'), 'success');
            await load();
            cancel();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('templates.errorArchive'), 'error');
        }
    };

    const unarchive = async () => {
        if (typeof selectedId !== 'number') return;
        try {
            await apiService.unarchiveTreatmentNoteTemplate(selectedId);
            addToast(t('templates.unarchived'), 'success');
            await load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('templates.errorUnarchive'), 'error');
        }
    };

    return (
        <div className="flex flex-col gap-4 p-6">
            <div className="flex items-center justify-between">
                <h1 className="text-lg font-semibold text-brand-text flex items-center gap-2">
                    <LayoutTemplate className="w-5 h-5 text-brand-primary" />
                    {t('templates.pageTitle')}
                </h1>
                <button
                    type="button"
                    onClick={startNew}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary text-white text-sm font-medium rounded-md hover:bg-brand-primary/90 cursor-pointer transition-colors"
                >
                    <Plus className="w-4 h-4" /> {t('templates.newTemplate')}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
                {/* Sidebar */}
                <aside className={cardClass + ' h-fit'}>
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-text-muted pointer-events-none" />
                        <input
                            type="search"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder={t('templates.searchPlaceholder')}
                            className={inputClass + ' pl-8 text-xs'}
                        />
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-brand-text-muted cursor-pointer mb-3 select-none">
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={e => setShowArchived(e.target.checked)}
                            className="rounded border-brand-border cursor-pointer"
                        />
                        {t('templates.showArchived')}
                    </label>

                    {loading ? (
                        <p className="text-sm text-brand-text-muted">{t('templates.loading')}</p>
                    ) : filtered.length === 0 ? (
                        <p className="text-sm text-brand-text-muted">{t('templates.noTemplates')}</p>
                    ) : (
                        <ul className="space-y-0.5 -mx-2">
                            {filtered.map(tpl => {
                                const active = selectedId === tpl.id;
                                return (
                                    <li key={tpl.id}>
                                        <button
                                            type="button"
                                            onClick={() => selectTemplate(tpl)}
                                            className={
                                                active
                                                    ? 'w-full text-left px-3 py-2 rounded-md bg-brand-primary/10 border border-brand-primary/30 cursor-pointer'
                                                    : 'w-full text-left px-3 py-2 rounded-md hover:bg-brand-surface-2 border border-transparent cursor-pointer transition-colors'
                                            }
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className={`text-sm truncate ${active ? 'font-semibold text-brand-text' : 'text-brand-text'}`}>
                                                    {pickLocale(tpl.name, 'de') || tpl.slug}
                                                </span>
                                                {tpl.archived && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-border/40 text-brand-text-muted shrink-0">
                                                        {t('templates.archivedBadge')}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-brand-text-muted mt-0.5 flex items-center gap-2">
                                                <span className="font-mono">{tpl.slug}</span>
                                                <span>·</span>
                                                <span>{CATEGORY_LABEL_KEY[tpl.category] ? t(CATEGORY_LABEL_KEY[tpl.category]) : tpl.category}</span>
                                                <span>·</span>
                                                <span>{tpl.sections?.length || 0} {t('templates.sectionsAbbr')}</span>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </aside>

                {/* Detail */}
                <div>
                    {selectedId === null ? (
                        <div className={cardClass + ' text-center py-16'}>
                            <LayoutTemplate className="w-10 h-10 text-brand-text-muted mx-auto mb-2 opacity-50" />
                            <p className="text-sm text-brand-text-muted">
                                {t('templates.emptyHint')}
                            </p>
                        </div>
                    ) : (
                        <TemplateDetailForm
                            draft={draft}
                            isNew={selectedId === 'new'}
                            indications={indications}
                            submitting={submitting}
                            onChange={setDraft}
                            onSave={save}
                            onArchive={archive}
                            onUnarchive={unarchive}
                            onCancel={cancel}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplatesAdminPage;
